/**
 * The two deliberate account actions on Settings › Payment (docs/features/
 * credit-control/06_ui_strategy.md §2, "Stop and lift dialogs"): stopping an
 * account and lifting its stop. Each asks for its own note (required) and says
 * in business words what it does and does not do, so neither is a form field.
 *
 * Presentational: the section owns the call and the error; these own the
 * fields. The wording of each reason arrives with the options.
 */
import { useState } from "react";
import type {
  CreditOption,
  StopReason,
} from "@shared-core/data/credit-control-types";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Textarea } from "../../primitives/textarea";

interface DialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The last failed attempt's message, shown in the dialog. */
  error: string | null;
  saving: boolean;
}

export function StopAccountDialog({
  open,
  onOpenChange,
  customerName,
  stopReasonOptions,
  error,
  saving,
  onConfirm,
}: DialogShellProps & {
  customerName: string;
  stopReasonOptions: CreditOption<StopReason>[];
  onConfirm: (input: { stopReason: StopReason; note: string }) => void;
}) {
  const [reason, setReason] = useState<StopReason>(
    stopReasonOptions[0]?.value ?? "overdue",
  );
  const [note, setNote] = useState("");
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setNote("");
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Put {customerName}'s credit account on stop</DialogTitle>
          <DialogDescription>
            New orders on credit account will wait for a credit decision. Card
            and pro-forma payments are not affected.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="stop-reason">Reason</Label>
          <Select
            value={reason}
            onValueChange={(v) => setReason(v as StopReason)}
          >
            <SelectTrigger id="stop-reason" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stopReasonOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stop-note">Note (required)</Label>
          <Textarea
            id="stop-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={note.trim() === "" || saving}
            onClick={() => onConfirm({ stopReason: reason, note: note.trim() })}
          >
            Put on stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LiftStopDialog({
  open,
  onOpenChange,
  customerName,
  waitingOrders,
  error,
  saving,
  onConfirm,
}: DialogShellProps & {
  customerName: string;
  /** Orders still waiting for a decision; lifting the stop does not release them. */
  waitingOrders: number;
  onConfirm: (input: { note: string }) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setNote("");
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Lift the stop on {customerName}'s credit account
          </DialogTitle>
          <DialogDescription>
            {waitingOrders > 0
              ? `${waitingOrders} ${waitingOrders === 1 ? "order is" : "orders are"} still waiting for a decision. Lifting the stop does not release ${waitingOrders === 1 ? "it" : "them"}.`
              : "New orders on account will go through again."}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="lift-note">Note (required)</Label>
          <Textarea
            id="lift-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={note.trim() === "" || saving}
            onClick={() => onConfirm({ note: note.trim() })}
          >
            Lift stop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
